import React, {useState, useEffect, useRef, useMemo} from 'react';

import SimpleCodeEditor from 'react-simple-code-editor';
import {wrap} from '@hrgui/chord-charts';
import {Textarea, Button, Menu} from '@mantine/core';
import {IconCaretDown, IconCaretDownFilled, IconCheck, IconChevronCompactDown, IconChevronDown, IconChevronsDown, IconDotsVertical, IconFoldDown, IconMusic, IconNote, IconNotes, IconPlus, IconSettings, IconX} from '@tabler/icons-react';

import {Actions} from '@/store/actions/actions';
import {useAction} from '@/store/kysely_module';
import {AttributeName, AttributeParentType} from '@/store/kysely_types';
import {parseTimestamps} from '@/modules/music_sniper/components/comments/comment_section';
import {useIsMobile} from '@/utils/user_agent';
import { useChordTranspose, parseTransposeSettings, parseChordLyricsData, TransposeSettings, ChordLyricsData } from '@/hooks/useChordTranspose';
// import {usePreventPeriodWithDoubleSpace} from '@/hooks/usePreventPeriodWithDoubleSpace';

export type AttributesBlocksProps = {
    values?: {description?: string, chord_lyrics?: string}
    parent_id: string;
    parent_type: AttributeParentType;
    refresh: () => void;
    workspace_id: string;
};

export const useAttributesBlocksState = (props: AttributesBlocksProps) => {
    const [queueDescriptionEdit, setQueueDescriptionEdit] = useState(false);
    const [queueChordLyricsEdit, setQueueChordLyricsEdit] = useState(false);

    useEffect(() => {
        if (!props.values?.description) {
            setQueueDescriptionEdit(false);
        }
    }, [props.values?.description]);

    useEffect(() => {
        if (!props.values?.chord_lyrics) {
            setQueueChordLyricsEdit(false);
        }
    }, [props.values?.chord_lyrics]);

    return {queueDescriptionEdit, setQueueDescriptionEdit, queueChordLyricsEdit, setQueueChordLyricsEdit};
};

type HookState = ReturnType<typeof useAttributesBlocksState>;

const emptyMenuItems: React.ReactNode[] = [];

export const AttributesBlocksHeader = (props: AttributesBlocksProps & {hookState: HookState; plusMenuItems?: React.ReactNode[], dotMenuItems?: React.ReactNode[]}) => {
    // const isMobile = useIsMobile();

    const {queueDescriptionEdit, setQueueDescriptionEdit, queueChordLyricsEdit, setQueueChordLyricsEdit} = props.hookState;

    const [debouncedEditBooleans, setDebouncedEditBooleans] = useState({queueDescriptionEdit, queueChordLyricsEdit});
    useEffect(() => {
        setTimeout(() => {
            setDebouncedEditBooleans({queueDescriptionEdit, queueChordLyricsEdit});
        }, 100);
    }, [queueDescriptionEdit, queueChordLyricsEdit]);

    const plusMenuItems = useMemo(() => {
        const items = [...(props.plusMenuItems || emptyMenuItems)];
        if (!props.values?.description && !debouncedEditBooleans.queueDescriptionEdit) {
            items.push(
                <Menu.Item
                    key='description'
                    leftSection={<IconNotes size={14} />}
                    onClick={() => setQueueDescriptionEdit(true)}
                >
                    Add Description
                </Menu.Item>
            );
        }

        if (!props.values?.chord_lyrics && !debouncedEditBooleans.queueChordLyricsEdit) {
            items.push(
                <Menu.Item
                key='chord_lyrics'
                    leftSection={<IconMusic size={14} />}
                    onClick={() => setQueueChordLyricsEdit(true)}
                >
                    Add Chords & Lyrics
                </Menu.Item>
            );
        }

        return items;
    }, [props.plusMenuItems, debouncedEditBooleans, queueDescriptionEdit, queueChordLyricsEdit, props.values?.description, props.values?.chord_lyrics]);

    // const showPlusMenu = isMobile;
    const showPlusMenu = Boolean(plusMenuItems.length);
    const showDotMenu = Boolean(props.dotMenuItems?.length);

    return (
        <div style={{
            // minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            // marginBottom: '10px',
            gap: '5px',
        }}>
            {showPlusMenu && (
                <Menu
                    closeOnItemClick={true}
                    zIndex={10000}
                    disabled={plusMenuItems.length === 0}
                >
                    <Menu.Target>
                        <Button
                            pl={5}
                            pr={5}
                            size='xs'
                            variant='subtle'
                            disabled={plusMenuItems.length === 0}
                        >
                            <IconPlus size={16} />
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {plusMenuItems}
                    </Menu.Dropdown>
                </Menu>
            )}

            {showDotMenu && (
                <Menu
                    closeOnItemClick={true}
                    zIndex={10000}
                    disabled={plusMenuItems.length === 0}
                >
                    <Menu.Target>
                        <Button
                            size='xs'
                            pl={5}
                            pr={5}
                            variant='subtle'
                            disabled={props.dotMenuItems?.length === 0}
                        >
                            <IconDotsVertical size={16} />
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {props.dotMenuItems}
                    </Menu.Dropdown>
                </Menu>
            )}

            {/* {!isMobile && !props.values?.description && !queueDescriptionEdit && (
                <Button
                    size='xs'
                    variant='subtle'
                    onClick={() => setQueueDescriptionEdit(true)}
                >
                    + Add Description
                </Button>
            )}

            {!isMobile && !props.values?.chord_lyrics && !queueChordLyricsEdit && (
                <Button
                    size='xs'
                    variant='subtle'
                    onClick={() => setQueueChordLyricsEdit(true)}
                >
                    + Add Chords & Lyrics
                </Button>
            )} */}
        </div>
    );
};

export const AttributesBlocksBody = (props: AttributesBlocksProps & {hookState: HookState}) => {
    const {queueDescriptionEdit, setQueueDescriptionEdit, queueChordLyricsEdit, setQueueChordLyricsEdit} = props.hookState;

    return (
        <>
            {
                (props.values?.description || queueDescriptionEdit) && (
                    <div
                    // style={{borderBottom: '1px solid'}}
                    >
                        <h3 style={{userSelect: 'none'}}>Description</h3>
                        <AttributeViewerAndEditor
                            name={AttributeName.description}
                            value={props.values?.description || ''}
                            parent_id={props.parent_id}
                            parent_type={props.parent_type}
                            workspace_id={props.workspace_id}
                            onCancel={() => {
                                setQueueDescriptionEdit(false);
                            }}
                            refresh={() => {
                                props.refresh();
                                setQueueDescriptionEdit(false);
                            }}
                            queueEdit={queueDescriptionEdit}
                        />
                    </div>
                )
            }

            {
                (props.values?.chord_lyrics || queueChordLyricsEdit) && (
                    <div
                    // style={{borderBottom: '1px solid'}}
                    >
                        <h3 style={{userSelect: 'none'}}>Lyrics</h3>
                        <AttributeViewerAndEditor
                            name='chord_lyrics'
                            value={props.values?.chord_lyrics || ''}
                            parent_id={props.parent_id}
                            parent_type={props.parent_type}
                            workspace_id={props.workspace_id}
                            onCancel={() => {
                                setQueueChordLyricsEdit(false);
                            }}
                            refresh={() => {
                                props.refresh();
                                setQueueChordLyricsEdit(false);
                            }}
                            queueEdit={queueChordLyricsEdit}
                        />
                    </div>
                )
            }
        </>
    );
}

type AttributeViewerAndEditorProps = {
    name: AttributeName;
    value: string;
    parent_id: string;
    parent_type: AttributeParentType;
    workspace_id: string;
    refresh: () => void;
    onCancel: () => void;
    queueEdit: boolean;
};

export const AttributeViewerAndEditor = (props: AttributeViewerAndEditorProps) => {
    // Parse chord lyrics data if it's a chord_lyrics attribute
    const chordLyricsData = props.name === 'chord_lyrics' ? parseChordLyricsData(props.value) : null;

    const [draft, setDraft] = useState(chordLyricsData?.lyrics || props.value);
    const [transposeSettings, setTransposeSettings] = useState<TransposeSettings>(() =>
        chordLyricsData?.transposeSettings || { transposeNumber: 0, shouldShowSharps: true }
    );
    const [isLocalEditing, setIsLocalEditing] = useState(false);

    const actions = {
        updateObjectAttribute: useAction('updateObjectAttribute'),
    } as Pick<Actions, 'updateObjectAttribute'>;

    useEffect(() => {
        if (props.name === 'chord_lyrics') {
            const data = parseChordLyricsData(props.value);
            setDraft(data.lyrics);
            setTransposeSettings(data.transposeSettings || { transposeNumber: 0, shouldShowSharps: true });
        } else {
            setDraft(props.value);
        }
    }, [props.value, props.name]);

    const handleSave = async () => {
        let valueToSave: string | null = draft;

        // For chord_lyrics, save as JSON structure, or null if empty
        if (props.name === 'chord_lyrics') {
            if (!draft.trim()) {
                // If lyrics are empty, set the whole attribute to null
                valueToSave = null;
            } else {
                valueToSave = JSON.stringify({
                    lyrics: draft,
                    transposeSettings: transposeSettings
                });
            }
        }

        await actions.updateObjectAttribute({
            parent_type: props.parent_type,
            parent_id: props.parent_id,
            attribute: props.name,
            value: valueToSave,
        }, {workspace_id: props.workspace_id});

        setIsLocalEditing(false);
        props.refresh();
    };

    const handleCancel = () => {
        if (props.name === 'chord_lyrics') {
            const data = parseChordLyricsData(props.value);
            setDraft(data.lyrics);
            setTransposeSettings(data.transposeSettings || { transposeNumber: 0, shouldShowSharps: true });
        } else {
            setDraft(props.value);
        }
        setIsLocalEditing(false);
        props.onCancel();
    };

    useEffect(() => {
        if (props.queueEdit) {
            if (props.name === 'chord_lyrics') {
                const data = parseChordLyricsData(props.value);
                setDraft(data.lyrics);
            } else {
                setDraft(props.value);
            }
            setTimeout(() => {
                wrapperRef.current?.querySelector('textarea')?.focus();
            }, 100);
        }
    }, [props.queueEdit, props.value, props.name]);

    // For chord_lyrics, compare the extracted lyrics with draft, otherwise compare directly
    const editing = props.name === 'chord_lyrics'
        ? chordLyricsData?.lyrics !== draft || isLocalEditing
        : props.value !== draft || isLocalEditing;

    const wrapperRef = useRef<HTMLDivElement>(null);

    const leftBorder = editing || props.queueEdit ? '1px solid #ccc' : '';

    const editor = props.name === 'chord_lyrics' ? (
        <div style={{padding: '12px'}}>
            <ChordLyricsEditor
                value={draft}
                onChange={(value) => {
                    setDraft(value);
                }}
                transposeSettings={transposeSettings}
                onTransposeSettingsChange={setTransposeSettings}
                isEditing={editing || props.queueEdit}
                onStartEditing={() => {
                    // Allow clicking to enter edit mode
                    if (!editing && !props.queueEdit) {
                        setIsLocalEditing(true);
                        setTimeout(() => {
                            wrapperRef.current?.querySelector('textarea')?.focus();
                        }, 100);
                    }
                }}
                onTransposeSave={async (newSettings: TransposeSettings) => {
                    // Save the updated JSON structure with new transpose settings
                    const updatedValue = JSON.stringify({
                        lyrics: draft,
                        transposeSettings: newSettings
                    });
                    await actions.updateObjectAttribute({
                        parent_type: props.parent_type,
                        parent_id: props.parent_id,
                        attribute: props.name,
                        value: updatedValue,
                    }, {workspace_id: props.workspace_id});
                }}
                workspaceId={props.workspace_id}
            />
        </div>
    ) : (
        <Textarea
            autosize
            minRows={3}
            classNames={{
                input: 'attribute-textarea',
            }}
            style={{
                maxWidth: '800px',
            }}
            value={draft}
            onChange={(e) => {
                setDraft(e.target.value);
            }}
        />
    )

    return (
        <div style={{position: 'relative', borderLeft: leftBorder}} ref={wrapperRef}>
            {editor}

            {(editing || props.queueEdit) && (
                <div style={{display: 'flex', gap: '5px'}}>
                    <Button onClick={handleSave} variant='subtle' size='xs'><IconCheck size={16} style={{marginRight: '5px'}} /> Save</Button>
                    <Button onClick={handleCancel} variant='subtle' size='xs'><IconX size={16} style={{marginRight: '5px'}} /> Cancel</Button>
                </div>
            )}
        </div>
    );
};


const ChordLyricsEditor = ({value, onChange, transposeSettings, onTransposeSettingsChange, isEditing, onStartEditing, onTransposeSave, workspaceId}: {
    value: string,
    onChange: (value: string) => void,
    transposeSettings: TransposeSettings,
    onTransposeSettingsChange?: (settings: TransposeSettings) => void,
    isEditing?: boolean,
    onStartEditing?: () => void,
    onTransposeSave?: (settings: TransposeSettings) => Promise<void>,
    workspaceId?: string
}) => {
    const { transposeNumber: transposeSteps, shouldShowSharps } = transposeSettings;

    // Use the hook for all transpose logic
    const {
        targetKey,
        transposedContent,
        handleTranspose,
        resetTranspose,
        toggleSharpsFlats,
        setTransposeSettings
    } = useChordTranspose(value, {
        initialSettings: transposeSettings,
        onSave: onTransposeSave,
        isEditing
    });

    // Keep parent component in sync
    useEffect(() => {
        if (onTransposeSettingsChange) {
            setTransposeSettings(transposeSettings);
        }
    }, [transposeSettings, onTransposeSettingsChange, setTransposeSettings]);

    // Update parent when local state changes
    const handleLocalTranspose = async (steps: number) => {
        await handleTranspose(steps);
        if (onTransposeSettingsChange) {
            const newSteps = Math.max(-11, Math.min(11, transposeSteps + steps));
            onTransposeSettingsChange({ ...transposeSettings, transposeNumber: newSteps });
        }
    };

    const handleLocalReset = async () => {
        await resetTranspose();
        if (onTransposeSettingsChange) {
            onTransposeSettingsChange({ ...transposeSettings, transposeNumber: 0 });
        }
    };

    const handleLocalToggleSharpsFlats = async () => {
        await toggleSharpsFlats();
        if (onTransposeSettingsChange) {
            onTransposeSettingsChange({ ...transposeSettings, shouldShowSharps: !shouldShowSharps });
        }
    };

    // Show display mode when not editing and has content
    if (!isEditing && value.trim()) {

        return (
            <div>
                {/* Minimal transpose controls - only show when needed */}
                {onTransposeSettingsChange && (
                    <div style={{
                        marginBottom: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 11,
                        opacity: 0.7
                    }}>
                        <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => handleLocalTranspose(-1)}
                            disabled={transposeSteps <= -11}
                            style={{
                                minWidth: 20,
                                height: 20,
                                fontSize: 10,
                                padding: '0 4px'
                            }}
                        >
                            -
                        </Button>
                        {/* Show transpose number between - and + */}
                        <span style={{ fontSize: 10, color: 'var(--mantine-color-dimmed)', minWidth: 20, textAlign: 'center' }}>
                            {transposeSteps === 0 ? '0' : (transposeSteps > 0 ? `+${transposeSteps}` : `${transposeSteps}`)}
                        </span>
                        <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => handleLocalTranspose(1)}
                            disabled={transposeSteps >= 11}
                            style={{
                                minWidth: 20,
                                height: 20,
                                fontSize: 10,
                                padding: '0 4px'
                            }}
                        >
                            +
                        </Button>
                        {/* Show additional controls when transposed */}
                        {transposeSteps !== 0 && (
                            <>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    onClick={handleLocalToggleSharpsFlats}
                                    style={{
                                        height: 18,
                                        fontSize: 9,
                                        padding: '0 6px'
                                    }}
                                >
                                    {shouldShowSharps ? '♯' : '♭'}
                                </Button>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    onClick={handleLocalReset}
                                    style={{
                                        height: 18,
                                        fontSize: 9,
                                        padding: '0 6px'
                                    }}
                                >
                                    Reset
                                </Button>
                            </>
                        )}
                    </div>
                )}

                {/* Chord chart display - clickable to edit */}
                <pre
                    onClick={onStartEditing}
                    style={{
                        fontFamily: '"Fira code", "Fira Mono", monospace',
                        fontSize: 14,
                        lineHeight: '1.4',
                        margin: 0,
                        padding: '10px 0',
                        whiteSpace: 'pre-wrap',
                        color: 'var(--input-color)',
                        cursor: onStartEditing ? 'text' : 'default',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: chordChartHighlight(transposedContent)
                    }}
                />
            </div>
        );
    }

    // Show editor mode
    return (
        <SimpleCodeEditor
            onValueChange={value => {
                // const newValue = handleChange(value);
                // if (newValue && newValue !== value) {
                    // onChange(newValue);
                    // return;
                // }

                onChange(value);
            }}
            value={value}
            highlight={(code) => chordChartHighlight(code)}
            textareaClassName='chord-lyrics-editor'
            style={{
                // padding: '5px',
                outline: 'none',
                color: 'var(--input-color)',
                minWidth: '100%',
                fontFamily: '"Fira code", "Fira Mono", monospace',
                // marginTop: 10,
                // marginBottom: 10,
                paddingBottom: 10,
                fontSize: 14,
                // borderBottom: '1px solid #ccc',
            }}
        />
    );
};

function chordChartHighlight(input: string) {
    let output = wrap(input, (x: string) => `<span class='highlighted-chord'>${x}</span>`);
    const timestamps = parseTimestamps(input);
    timestamps.forEach(timestamp => {
        // output = output.replace(timestamp, `<span class='highlighted-timestamp' onclick="alert('yo')">${timestamp}</span>`);
    });
    return output;
}
